import MainHeading from "@/components/common/MainHeading";
import Layout from "@/components/layout/Layout";
import { LeaveService } from "@/services/Leave/LeaveSetting";
import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ThrowError } from "@/api/callApi";
import type { TableColumn } from "@/components/common/CommonTable";
import { Button } from "@/components/ui/button";
import { RoutesLink } from "@/utility/RouteLinks";
import { Plus, Trash, TriangleAlert, Pencil, Calendar } from "lucide-react";
import TableActions from "@/components/common/TableActions";
import CommonTable from "@/components/common/CommonTable";
import CommonModal from "@/components/common/CommonModal";
import type { ILeave, ILeaveOverview } from "@/interface/leave";
import moment from "moment";
import CommonFilter from "@/components/common/CommonFilter";
import type { IFilter, IPageInfo } from "@/interface/common.interface";
import { GetFilterUrl, showToastNotification } from "@/utility/helper";
import TablePagination from "@/components/common/TablePagination";
import { useCurrentUser } from "@/hooks/useCurrentUser";

const DURATION_OPTIONS = [
  { label: "All Duration", value: "" },
  { label: "Full Day", value: "FULL_DAY" },
  { label: "Half Day", value: "HALF_DAY" },
  { label: "First Half", value: "FIRST_HALF" },
  { label: "Second Half", value: "SECOND_HALF" },
];

const STATUS_OPTIONS = [
  { label: "All Status", value: "" },
  { label: "Pending", value: "PENDING" },
  { label: "Approved", value: "APPROVED" },
  { label: "Rejected", value: "REJECTED" },
  { label: "Cancelled", value: "CANCELLED" },
];

const MyLeave: React.FC = () => {
  const navigate = useNavigate();
  const lastFetchedYear = useRef<number | null>(null);
  const { userId, siteId } = useCurrentUser();
  const [leaves, setLeaves] = useState<ILeave[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string>("");
  const [overview, setOverview] = useState<ILeaveOverview | null>(null);

  const [filter, setFilter] = useState<IFilter>({
    // search: "",
    startDate: moment().startOf("month").format("YYYY-MM-DD"),
    endDate: moment().endOf("month").format("YYYY-MM-DD"),
    duration: "",
    status: "",
    siteId: siteId,
    userId: userId,
  });

  const [paginationData, setPaginationData] = useState<IPageInfo>({
    currentPage: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0,
  });

  const handleChangePageSize = (value: number) => {
    setPaginationData((prev) => ({ ...prev, pageSize: value, currentPage: 1 }));
  };

  const toggleDeleteDialog = useCallback((id: string = "") => {
    setDeleteId(id ?? "");
    setDeleteDialogOpen((prev) => !prev);
  }, []);

  // const handleSearchChange = useCallback((value: string) => {
  //   setFilter((prev) => {
  //     if (prev.search === value) return prev;
  //     return { ...prev, search: value };
  //   });
  // }, []);

  const handleFilterChange = useCallback((value: string, name: string) => {
    setFilter((prev) => ({
      ...prev,
      [name]: value,
    }));

    setPaginationData((prev) => ({
      ...prev,
      currentPage: 1,
    }));
  }, []);

  const handleDateRangeChange = useCallback((start: string, end: string) => {
    setFilter((prev) => ({
      ...prev,
      startDate: start,
      endDate: end,
    }));

    setPaginationData((prev) => ({
      ...prev,
      currentPage: 1,
    }));
  }, []);

  const handleGetLeaves = async (
    currentPage = paginationData.currentPage,
    currentLimit = paginationData.pageSize,
  ) => {
    setIsLoading(true);
    try {
      // const { duration, status, ...restFilter } = filter;

      // const apiFilter: Record<string, any> = {
      //   ...restFilter,

      //   // type: "team-data",
      // };

      // if (duration) apiFilter.duration = duration;
      // if (status) apiFilter.status = status;

      const params = GetFilterUrl({ ...filter });
      // console.log(params)

      const PATH = `type=my-data&pageNumber=${currentPage}&pageSize=${currentLimit}${params ? `&${params}` : ""}`;
      const response = await LeaveService.getLeaves(PATH);
      if (response?.status) {
        const allLeaves = response?.data?.leaves || response?.data || [];
        const userLeaves = Array.isArray(allLeaves)
          ? allLeaves.filter((l: ILeave) => !userId || l.userId === userId)
          : [];
        setLeaves(userLeaves);
        setPaginationData((prev) => ({
          ...prev,
          total: response?.data?.pageInfo?.total || 0,
          totalPages: response?.data?.pageInfo?.totalPages || 0,
        }));
      }
    } catch (error) {
      ThrowError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchOverview = useCallback(async () => {
    if (!userId) return;

    let year = new Date().getFullYear();

    if (filter.startDate) {
      const parsed = moment(filter.startDate);
      if (parsed.isValid()) {
        year = parsed.year();
      }
    }

    if (lastFetchedYear.current === year) {
      return;
    }

    lastFetchedYear.current = year;

    try {
      const response = await LeaveService.getLeaveOverview(
        userId,
        siteId,
        year,
      );

      if (response?.status) {
        setOverview(response.data);
      }
    } catch (error) {
      lastFetchedYear.current = null;
      ThrowError(error);
    }
  }, [userId, siteId, filter.startDate]);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  useEffect(() => {
    handleGetLeaves(paginationData.currentPage, paginationData.pageSize);
  }, [
    userId,
    siteId,
    paginationData.currentPage,
    paginationData.pageSize,
    // filter.search,
    filter.startDate,
    filter.endDate,
    filter.duration,
    filter.status,
  ]);

  const handleDeleteLeave = useCallback(async () => {
    try {
      const response = await LeaveService.deleteLeave(deleteId, siteId);
      if (response?.status) {
        showToastNotification(
          "success",
          response?.message || "Leave deleted successfully",
        );
        toggleDeleteDialog("");
        handleGetLeaves();
      } else {
        showToastNotification(
          "danger",
          response?.message || "Failed to delete leave",
        );
      }
    } catch (error) {
      ThrowError(error);
    }
  }, [deleteId, toggleDeleteDialog]);
  const columns: TableColumn<ILeave>[] = [
    {
      key: "srNo",
      header: "Sr No",
      render: (_item: ILeave, index: number) => (
        <span className="text-muted-foreground font-medium"># {index + 1}</span>
      ),
    },
    {
      key: "Name",
      header: "Name",
      render: (item: ILeave) => (
        <span className="font-semibold capitalize">
          {item?.user?.name || "—"}
        </span>
      ),
    },
    // {
    //     key: 'site',
    //     header: 'Site',
    //     render: (item: ILeave) => (
    //         <span className="font-semibold capitalize">{item?.site?.name || "—"}</span>
    //     )
    // },
    // {
    //     key: 'branch',
    //     header: 'Branch',
    //     render: (item: ILeave) => (
    //         <span className="font-semibold capitalize">{item?.branch?.name || "—"}</span>
    //     )
    // },
    {
      key: "leaveType",
      header: "Leave Type",
      render: (item: ILeave) => (
        <span className="font-medium">{item?.leaveType || "—"}</span>
      ),
    },
    {
      key: "startDate",
      header: "Start Date",
      render: (item: ILeave) => (
        <span className="font-medium">
          {item?.startDate ? moment(item.startDate).format("DD-MM-YYYY") : "—"}
        </span>
      ),
    },
    {
      key: "endDate",
      header: "End Date",
      render: (item: ILeave) => (
        <span className="font-medium">
          {item?.endDate ? moment(item.endDate).format("DD-MM-YYYY") : "—"}
        </span>
      ),
    },
    {
      key: "duration",
      header: "Duration",
      render: (item: ILeave) => (
        <span className="font-medium capitalize text-slate-600">
          {item?.duration ? item.duration.replace(/_/g, " ") : "—"}
        </span>
      ),
    },
    {
      key: "numberOfDays",
      header: "Days",
      render: (item: ILeave) => (
        <span className="font-medium">{item?.numberOfDays || 0}</span>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (item: ILeave) => {
        const status = (item?.status || "pending").toLowerCase();
        let statusClasses = "text-amber-600 border-amber-600 bg-amber-50";
        if (status === "approved" || status === "active") {
          statusClasses = "text-green-600 border-green-600 bg-green-50";
        } else if (status === "rejected" || status === "cancelled") {
          statusClasses =
            "text-destructive border-destructive bg-destructive/10";
        }
        return (
          <span
            className={`inline-flex items-center px-2 py-0.5 text-xs font-medium border rounded-full capitalize ${statusClasses}`}
          >
            {status}
          </span>
        );
      },
    },
    {
      key: "actions",
      header: "Actions",
      render: (item: ILeave) => {
        const isPending =
          (item?.status || "pending").toLowerCase() === "pending";
        // const userId = item?.id;
        return (
          <TableActions
            onView={() => {
              if (item?.id) navigate(`${RoutesLink.leaveDetail}/${item.id}`);
            }}
            viewLabel="View Details"
            actions={[
              {
                label: "Edit Leave",
                onClick: () =>
                  navigate(`${RoutesLink.editLeave}/${item?.id}`, {
                    state: { leaveBalances: overview },
                  }),
                icon: <Pencil className="h-4 w-4" />,
                show: isPending,
              },
              {
                label: "Delete Leave",
                onClick: () => {
                  if (item?.id) toggleDeleteDialog(item.id);
                },
                icon: <Trash className="h-4 w-4" />,
                className: "text-red-600",
                show: isPending,
              },
            ]}
          />
        );
      },
    },
  ];

  return (
    <Layout>
      <div className="space-y-6">
        <MainHeading
          title="My Leaves"
          buttonText="Apply Leave"
          buttonIcon={<Plus size={18} />}
          isBackButtonVisible={true}
          onClick={() =>
            navigate(RoutesLink.addLeave, {
              state: { leaveBalances: overview },
            })
          }
        />

        {overview && (
          <div className="space-y-4">
            <h3 className="text-base font-semibold text-slate-700 tracking-wide uppercase">
              Leave Balances
            </h3>

            {/* Leave type cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {(overview.leaves.length > 0
                ? overview.leaves
                : [
                  {
                    type: "NO_LEAVE",
                    label: "No Leave Balance",
                    available: 0,
                    used: 0,
                    total: 0,
                  },
                ]
              ).map((leave, idx) => {
                const palettes = [
                  {
                    gradient: "from-violet-500 to-indigo-500",
                    light: "bg-violet-50",
                    text: "text-violet-600",
                    border: "border-violet-100",
                    progress: "bg-violet-500",
                    icon: "bg-violet-100",
                  },
                  {
                    gradient: "from-emerald-500 to-teal-500",
                    light: "bg-emerald-50",
                    text: "text-emerald-600",
                    border: "border-emerald-100",
                    progress: "bg-emerald-500",
                    icon: "bg-emerald-100",
                  },
                  {
                    gradient: "from-amber-500 to-orange-500",
                    light: "bg-amber-50",
                    text: "text-amber-600",
                    border: "border-amber-100",
                    progress: "bg-amber-500",
                    icon: "bg-amber-100",
                  },
                  {
                    gradient: "from-rose-500 to-pink-500",
                    light: "bg-rose-50",
                    text: "text-rose-600",
                    border: "border-rose-100",
                    progress: "bg-rose-500",
                    icon: "bg-rose-100",
                  },
                  {
                    gradient: "from-sky-500 to-cyan-500",
                    light: "bg-sky-50",
                    text: "text-sky-600",
                    border: "border-sky-100",
                    progress: "bg-sky-500",
                    icon: "bg-sky-100",
                  },
                  {
                    gradient: "from-fuchsia-500 to-purple-500",
                    light: "bg-fuchsia-50",
                    text: "text-fuchsia-600",
                    border: "border-fuchsia-100",
                    progress: "bg-fuchsia-500",
                    icon: "bg-fuchsia-100",
                  },
                ];
                const palette = palettes[idx % palettes.length];
                const usedPercent =
                  leave.total > 0
                    ? Math.min(
                      100,
                      Math.round((leave.used / leave.total) * 100),
                    )
                    : 0;

                return (
                  <div
                    key={leave.type}
                    className={`relative bg-white border ${palette.border} rounded-2xl p-5 shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden group`}
                  >
                    {/* Top gradient stripe */}
                    <div
                      className={`absolute top-0 left-0 right-0 h-1 rounded-t-2xl bg-linear-to-r ${palette.gradient}`}
                    />

                    {/* Header row */}
                    <div className="flex items-start justify-between mt-1">
                      <div
                        className={`w-10 h-10 rounded-xl ${palette.icon} flex items-center justify-center shrink-0`}
                      >
                        <Calendar className={`w-5 h-5 ${palette.text}`} />
                      </div>
                      <span
                        className={`text-xs font-semibold px-2 py-1 rounded-full ${palette.light} ${palette.text}`}
                      >
                        {leave.total} Total
                      </span>
                    </div>

                    <div className="flex justify-between flex-wrap items-center mt-3">
                      {/* Leave label */}
                      <p className="text-sm font-medium text-slate-500 leading-tight">
                        {leave.label}
                      </p>

                      {/* Available count */}
                      <p className={`font-extrabold mt-1 ${palette.text}`}>
                        {leave.available}
                        <span className="text-sm font-normal text-slate-400 ml-1">
                          available
                        </span>
                      </p>
                    </div>

                    {/* Progress bar */}
                    <div className="mt-4">
                      <div className="flex justify-between text-xs text-slate-400 mb-1">
                        <span>Used</span>
                        <span>{usedPercent}%</span>
                      </div>
                      <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${palette.progress} transition-all duration-500`}
                          style={{ width: `${usedPercent}%` }}
                        />
                      </div>
                    </div>

                    {/* Stats row */}
                    <div className="mt-3 flex justify-between">
                      <div className="text-center">
                        <p className="text-xs text-slate-400">Used</p>
                        <p className={`text-sm font-bold ${palette.text}`}>
                          {leave.used}
                        </p>
                      </div>
                      <div className="w-px bg-slate-100" />
                      <div className="text-center">
                        <p className="text-xs text-slate-400">Available</p>
                        <p className={`text-sm font-bold ${palette.text}`}>
                          {leave.available}
                        </p>
                      </div>
                      <div className="w-px bg-slate-100" />
                      <div className="text-center">
                        <p className="text-xs text-slate-400">Total</p>
                        <p className={`text-sm font-bold ${palette.text}`}>
                          {leave.total}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <CommonFilter
          startDate={filter.startDate || ""}
          endDate={filter.endDate || ""}
          onDateRangeChange={handleDateRangeChange}
          value={filter}
          filterOptions={DURATION_OPTIONS}
          filterName="duration"
          filterPlaceholder="Select Duration"
          sortOptions={STATUS_OPTIONS}
          sortName="status"
          sortPlaceholder="All Status"
          onChange={handleFilterChange}
        />
        <CommonTable columns={columns} data={leaves} loading={isLoading} />
        <TablePagination
          paginationData={paginationData}
          setPaginationData={setPaginationData}
          handleChangePageSize={handleChangePageSize}
          isLoading={isLoading}
        />

        <CommonModal
          show={deleteDialogOpen}
          onClose={() => toggleDeleteDialog("")}
          title=""
          className="max-w-md rounded-2xl p-0 overflow-hidden"
        >
          <div className="p-6">
            <div className="flex flex-col items-center text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
                <TriangleAlert className="text-red-600 w-8 h-8" />
              </div>
              <h2 className="mt-5 text-2xl font-bold text-gray-900">
                Delete Applied Leave?
              </h2>
              <p className="mt-3 text-sm leading-6 text-gray-500">
                This action cannot be undone. Once deleted, the leave
                application will be permanently removed.
              </p>
            </div>
            <div className="mt-8 flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => toggleDeleteDialog("")}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                className="flex-1"
                onClick={handleDeleteLeave}
              >
                Delete
              </Button>
            </div>
          </div>
        </CommonModal>
      </div>
    </Layout>
  );
};

export default MyLeave;
